// src/services/api/login/authService.ts
import api from "../axiosConfig";

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  requireOtp?: boolean;
  user: any;
  email: string;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken?: string;
}

export const login = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>("/auth/login", {
      email,
      password,
    });
    localStorage.setItem("accessToken", response.data.accessToken);
    localStorage.setItem("refreshToken", response.data.refreshToken);
    api.defaults.headers.common["Authorization"] =
      `Bearer ${response.data.accessToken}`;
    return response.data;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

export const refreshToken = async (): Promise<string | null> => {
  const currentRefreshToken = localStorage.getItem("refreshToken");
  if (!currentRefreshToken) {
    return null;
  }

  try {
    const response = await api.post<RefreshResponse>("/auth/refresh-token", {
      refreshToken: currentRefreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem("accessToken", accessToken);
    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
    }

    api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    return accessToken;
  } catch (error) {
    console.error("Failed to refresh token:", error);
    return null;
  }
};

export const logout = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  try {
    if (refreshToken) {
      await api.post("/auth/logout", { refreshToken });
    }
  } catch (error) {
    console.error("Logout API failed:", error);
  }
  localStorage.clear();
  delete api.defaults.headers.common["Authorization"];
  if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
};

export const getAccessToken = (): string | null => {
  if (typeof window !== "undefined") {
    const rawUser = localStorage.getItem("user");
    try {
      if (!rawUser || rawUser === "undefined") return null;
      const parsedUser = JSON.parse(rawUser);
      return parsedUser?.accessToken || null;
    } catch (error) {
      console.error("Invalid user JSON in localStorage:", error);
      return null;
    }
  }
  return null;
};

export const getRefreshToken = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("refreshToken");
  }
  return null;
};

export const verifyOtp = async (
  email: string,
  otp: string
): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>("/auth/verify-otp", {
      email,
      otp,
    });
    localStorage.setItem("accessToken", response.data.accessToken);
    localStorage.setItem("refreshToken", response.data.refreshToken);
    api.defaults.headers.common["Authorization"] =
      `Bearer ${response.data.accessToken}`;
    return response.data;
  } catch (error) {
    console.error("OTP verification failed:", error);
    throw error;
  }
};

export const requestPasswordReset = async (email: string) => {
  try {
    const response = await api.post("/auth/forgot-password", { email });
    return response.data;
  } catch (error) {
    console.error("Error requesting password change:", error);
    throw error;
  }
};

// export enum OtpType {
//   TWO_FACTOR_AUTH = 1,
//   PASSWORD_RESET = 2,
//   EMAIL_VERIFICATION = 3,
//   PASSWORD_CHANGE = 4,
// }

export const verifyOtpCode = async (email: string, otp: string) => {
  try {
    const response = await api.post("/auth/validate-otp", {
      email,
      otp,
      otpType: 2,
    });
    return response.data;
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw error;
  }
};

export const resetPassword = async (newPassword: string): Promise<any> => {
  const email = localStorage.getItem("resetEmail");
  const otp = localStorage.getItem("resetOtp");

  if (!email || !otp) {
    throw new Error("Missing email or OTP from localStorage");
  }

  try {
    const response = await api.post("/auth/reset-password", {
      email,
      newPassword,
      otp,
    });
    localStorage.removeItem("resetEmail");
    localStorage.removeItem("resetOtp");
    return response.data;
  } catch (error) {
    console.error("Reset password failed:", error);
    throw error;
  }
};

export const RequestPasswordChange = async () => {
  try {
    const response = await api.post("/profiles/request-password-change");
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const ChangePassword = async (data: {
  oldPassword: any;
  newPassword: any;
  otp?: any;
}) => {
  try {
    const response = await api.post("/profiles/password", data);
    return response.data;
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};
